import { action, computed, observable } from 'mobx';
import { isEqual } from 'lodash';
import { message } from 'antd';
import type ApplicationDefinitionConditionalEditStore from './ApplicationDefinitionConditionalEditStore';
import ApplicationDefinitionConditionalService from '../services/ApplicationDefinitionConditionalService';
import { ApplicationDefinitionConditional } from '../../iota_applications/types/ApplicationDefinition';
import { AdditionalClassificationDto, ConditionDto, InputGroupDto } from '../types';
import { TabImportGroupMapping, TabImportRequest, TabImportResponse } from '../types/IotaTabImport';
import { CapturedTabImport, ReadyTabImport } from '../types/TabImportDraft';
import { getTabStructureType } from '../misc/IotaTabExporter';
import { formatClassificationConflicts } from '../misc/classificationImpact';
import { validateConditions, validateRows } from '../misc/tabImportValidation';

type PendingContext = {
    application: ApplicationDefinitionConditional;
    projectId: string;
    request: TabImportRequest;
    validated?: ReadyTabImport;
};
type PendingTabImport = PendingContext & CapturedTabImport;
type Phase = 'idle' | 'validating' | 'mapping' | 'confirming' | 'committing';

const errorText = (error: unknown, fallback: string) => (error instanceof Error ? error.message : fallback);

function readTabFile(file: File): Promise<string> {
    return new Promise((resolve, reject) => {
        const reader = new FileReader();
        reader.onload = () =>
            typeof reader.result === 'string'
                ? resolve(reader.result)
                : reject(new Error('Could not read the tab file.'));
        reader.onerror = () => reject(new Error('Could not read the tab file.'));
        reader.readAsText(file);
    });
}

function readTabContent(captured: CapturedTabImport, response: TabImportResponse): ReadyTabImport {
    switch (captured.kind) {
        case 'additional-classifications':
            if (!response.additionalClassifications || !response.meta) {
                throw new Error('Classification import returned no content or metadata. Import the file again.');
            }
            return {
                kind: captured.kind,
                targetSnapshot: captured.targetSnapshot,
                content: response.additionalClassifications,
                meta: response.meta
            };
        case 'conditions':
            if (!response.conditions) {
                throw new Error('Conditions import returned no content. Import the file again.');
            }
            return { kind: captured.kind, targetSnapshot: captured.targetSnapshot, content: response.conditions };
        case 'input-group':
            if (!response.inputGroup) {
                throw new Error('Input-group import returned no content. Import the file again.');
            }
            return {
                kind: captured.kind,
                targetSnapshot: captured.targetSnapshot,
                targetId: captured.targetId,
                content: response.inputGroup
            };
        // no default
    }
}

export default class TabImportStore {
    @observable phase: Phase = 'idle';
    @observable mappings: TabImportGroupMapping[] = [];
    @observable selectedMappings: Record<string, string> = {};
    private pending?: PendingTabImport;
    private generation = 0;

    constructor(
        private readonly edit: ApplicationDefinitionConditionalEditStore,
        private readonly service: ApplicationDefinitionConditionalService
    ) {}

    @computed get isBusy() {
        return this.phase !== 'idle';
    }
    @computed get isImporting() {
        return this.phase === 'validating' || this.isCommitting;
    }
    @computed get isCommitting() {
        return this.phase === 'committing';
    }
    @computed get isConfirmVisible() {
        return this.phase === 'confirming';
    }
    @computed get isMappingVisible() {
        return this.phase === 'mapping';
    }

    @action.bound
    dispose() {
        this.generation++;
        this.reset();
    }

    @action.bound
    cancel() {
        if (!this.isImporting) this.reset();
    }

    @action.bound
    private reset() {
        this.pending = undefined;
        this.phase = 'idle';
        this.mappings = [];
        this.selectedMappings = {};
    }

    @action.bound
    async importTab(file: File, tabKey: string = this.edit.activeTab) {
        const application = this.edit.applicationDefinition;
        const project = this.edit.project;
        const target = this.edit.inputGroups.find(group => group.tabKey === tabKey);
        if (!application || !project || this.isBusy || this.edit.isUpdating) return;
        const generation = this.generation;
        try {
            if (!file.name.endsWith('.iota_tab')) {
                throw new Error('Choose a .iota_tab file. Use Import bindings for combined .iota_conditions files.');
            }
            if (!target && tabKey !== 'conditions' && tabKey !== 'additional-classifications') {
                throw new Error('Select a supported destination tab.');
            }
            const captured: CapturedTabImport = target
                ? {
                      kind: 'input-group',
                      targetId: target.inputGroupId,
                      targetSnapshot: this.edit.getGroupDraftSnapshot(target)
                  }
                : tabKey === 'conditions'
                  ? { kind: 'conditions', targetSnapshot: this.edit.getConditionsSnapshot() }
                  : {
                        kind: 'additional-classifications',
                        targetSnapshot: this.edit.getClassificationsSnapshot()
                    };
            if (captured.kind === 'input-group') {
                getTabStructureType(captured.targetSnapshot);
            }
            this.phase = 'validating';
            const text = await readTabFile(file);
            if (generation !== this.generation || this.edit.applicationDefinition !== application) return;
            let parsed: unknown;
            try {
                parsed = JSON.parse(text);
            } catch {
                throw new Error('The tab file is not valid JSON.');
            }
            const request: TabImportRequest = {
                file: parsed,
                targetConfigurationType: captured.kind,
                ...(captured.kind === 'input-group'
                    ? { targetInputGroup: { ...captured.targetSnapshot, bindingGroups: [] } }
                    : {}),
                expectedTabImportRevision: application.tabImportRevision ?? ''
            };
            this.pending = { ...captured, application, projectId: project.id, request };
            await this.validatePending();
        } catch (error) {
            if (generation === this.generation) {
                this.reset();
                message.error(errorText(error, 'Tab import failed. Please try again.'));
            }
        }
    }

    @action.bound
    setMapping(reference: string, inputGroupId: string) {
        this.selectedMappings = { ...this.selectedMappings, [reference]: inputGroupId };
    }

    @action.bound
    async confirmMappings() {
        const pending = this.pending;
        if (!pending || this.isImporting) return;
        const generation = this.generation;
        try {
            for (const mapping of this.mappings) {
                if (
                    !mapping.candidates.some(
                        candidate => candidate.inputGroupId === this.selectedMappings[mapping.reference]
                    )
                ) {
                    throw new Error(
                        `Select a compatible destination for "${mapping.name}" (${mapping.structureType}).`
                    );
                }
            }
            pending.request = {
                ...pending.request,
                inputGroupMappings: { ...pending.request.inputGroupMappings, ...this.selectedMappings }
            };
            this.phase = 'validating';
            await this.validatePending();
        } catch (error) {
            if (generation === this.generation) {
                message.error(errorText(error, 'Conditions mapping failed. Please try again.'));
            }
        }
    }

    private validateTarget(pending: PendingTabImport) {
        if (this.edit.applicationDefinition !== pending.application) {
            throw new Error('The application changed while import was pending. Import the file again.');
        }
        let current: InputGroupDto | ConditionDto[] | AdditionalClassificationDto[] | undefined;
        switch (pending.kind) {
            case 'conditions':
                current = this.edit.getConditionsSnapshot();
                break;
            case 'additional-classifications':
                current = this.edit.getClassificationsSnapshot();
                break;
            case 'input-group': {
                const target = this.edit.inputGroups.find(group => group.inputGroupId === pending.targetId);
                current = target && this.edit.getGroupDraftSnapshot(target);
            }
        }
        if (!isEqual(current, pending.targetSnapshot)) {
            throw new Error('The target tab changed while import was pending. Import the file again.');
        }
    }

    private assertClassificationDependencies(classifications: AdditionalClassificationDto[]) {
        const conflicts = this.edit.getClassificationConflicts(classifications);
        if (conflicts.length) {
            throw new Error(formatClassificationConflicts(conflicts, 'importing'));
        }
    }

    private validateContent(application: ApplicationDefinitionConditional, imported: ReadyTabImport): void {
        const visual = this.edit.applicationDefinitionEditVisualStore;
        switch (imported.kind) {
            case 'additional-classifications':
                this.assertClassificationDependencies(imported.content);
                break;
            case 'conditions':
                validateConditions(imported.content, application.inputGroups, this.edit.inputGroups, visual.ruleTags);
                break;
            case 'input-group': {
                const target = this.edit.inputGroups.find(group => group.inputGroupId === imported.targetId);
                if (!target) {
                    throw new Error('Input-group import target is unavailable. Import the file again.');
                }
                validateRows(
                    imported.content.bindingGroups,
                    target.bindingGroups,
                    new Set(visual.ruleTags.map(tag => tag.id)),
                    new Set((visual.environmentVariables ?? []).map(variable => variable.id))
                );
            }
        }
    }

    private hasDraftContent(pending: PendingTabImport): boolean {
        switch (pending.kind) {
            case 'additional-classifications':
                return this.edit.additionalClassifications.length > 0;
            case 'conditions':
                return this.edit.conditions.length > 0;
            case 'input-group':
                return Boolean(
                    this.edit.inputGroups.find(group => group.inputGroupId === pending.targetId)?.dto.bindingGroups
                        .length
                );
            // no default
        }
    }

    private async validatePending() {
        const pending = this.pending;
        if (!pending) return;
        try {
            const response = await this.service.validateTab(pending.projectId, pending.application.id, pending.request);
            if (this.pending !== pending) return;
            if (response.isErr())
                throw new Error(response.error.data?.title ?? 'Tab validation failed. Please try again.');
            this.validateTarget(pending);
            if (response.value.unresolvedInputGroups?.length) {
                this.mappings = response.value.unresolvedInputGroups.map(mapping => {
                    const candidates = mapping.candidates.filter(candidate =>
                        this.edit.inputGroups.some(
                            group =>
                                group.inputGroupId === candidate.inputGroupId &&
                                getTabStructureType(group) === mapping.structureType
                        )
                    );
                    if (!candidates.length) {
                        throw new Error(
                            `Reference "${mapping.name}" (${mapping.structureType}) has no compatible saved and displayed destination group.`
                        );
                    }
                    return { ...mapping, candidates };
                });
                this.selectedMappings = {};
                this.phase = 'mapping';
                return;
            }
            this.mappings = [];
            const imported = readTabContent(pending, response.value);
            this.validateContent(pending.application, imported);
            pending.validated = imported;
            pending.request = { ...pending.request, validationToken: response.value.validationToken };
            if (response.value.hasConfiguredContent || this.hasDraftContent(pending)) this.phase = 'confirming';
            else await this.confirm();
        } catch (error) {
            if (this.pending === pending) this.reset();
            throw error;
        }
    }

    @action.bound
    async confirm() {
        const pending = this.pending;
        if (!pending?.validated || this.edit.applicationDefinition !== pending.application || this.isCommitting) return;
        try {
            this.validateTarget(pending);
            this.validateContent(pending.application, pending.validated);
            this.phase = 'committing';
            const response = await this.service.commitTab(pending.projectId, pending.application.id, pending.request);
            if (this.pending !== pending || this.edit.applicationDefinition !== pending.application) return;
            if (response.isErr())
                throw new Error(response.error.data?.title ?? 'Tab import could not be saved. Please try again.');
            this.applyCommitted(pending, response.value);
            message.success('Tab configuration has been imported and saved.');
        } catch (error) {
            if (this.pending === pending) message.error(errorText(error, 'Tab import failed. Please try again.'));
        } finally {
            if (this.pending === pending) this.reset();
        }
    }

    private applyCommitted(pending: PendingTabImport, saved: TabImportResponse) {
        const application = pending.application;
        const imported = readTabContent(pending, saved);
        // Only saved application metadata is owned by the import workflow. Drafts remain in the edit store.
        switch (imported.kind) {
            case 'additional-classifications':
                application.additionalClassifications = imported.content;
                application.meta = imported.meta;
                break;
            case 'conditions':
                application.conditions = imported.content;
                break;
            case 'input-group': {
                const savedGroups = [...application.inputGroups];
                const index = savedGroups.findIndex(item => item.inputGroupId === imported.content.inputGroupId);
                if (index < 0) savedGroups.push(imported.content);
                else savedGroups[index] = imported.content;
                application.inputGroups = savedGroups;
            }
        }
        application.tabImportRevision = saved.tabImportRevision;
        application.setLastUpdatedInfo(saved.lastUpdatedBy, saved.lastUpdatedTime ?? undefined);
        this.edit.applyCommittedTab(imported);
    }
}
